import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import React from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/main.css';
import logo from './logo.png';
import { FiHome, FiMapPin, FiCheckSquare, FiUser as FiUserOutline, FiLogOut, FiUsers as FiUsersOutline, FiFileText, FiRefreshCw, FiBox, FiShoppingCart as FiShoppingCartOutline, FiSettings, FiSliders, FiCreditCard, FiUserCheck, FiFile, FiClipboard, FiActivity, FiBookOpen, FiTrendingDown, FiShoppingBag } from 'react-icons/fi';
import ScrollToTop from './components/ScrollToTop';
import SiteNav from './components/SiteNav';
import SiteFooter from './components/SiteFooter';
import { ContactModalProvider } from './context/ContactModalContext';
import IntegrationConsentModal from './components/IntegrationConsentModal';
import RequireFeature from './components/RequireFeature';
import useFeatureAccess from './hooks/useFeatureAccess';
import usePendingIntegrationConsents from './hooks/usePendingIntegrationConsents';
import api, { PublicOrderTemplatesAPI } from './services/api';
// Page Components
import Home from './pages/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import ContactUs from './pages/ContactUs';
import AccountDeletion from './pages/AccountDeletion';
import ComplaintForm from './pages/ComplaintForm';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Account from './pages/Account';
import Tasks from './pages/Tasks';
import LocationPage from './pages/LocationPage';
import Admin from './pages/Admin';
import RefundPolicy from './pages/RefundPolicy';
import Pricing from './pages/Pricing';
import OtherProducts from './pages/OtherProducts';
import Invoices from './pages/Invoices';
import Quotes from './pages/Quotes';
import Configurations from './pages/Configurations';
import InvoiceForm from './pages/InvoiceForm';
import QuoteForm from './pages/QuoteForm';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Inventory from './pages/Inventory';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import OrderTemplateForm from './pages/OrderTemplateForm';
import PlaceOrder from './pages/PlaceOrder';
import NotFound from './pages/NotFound';
import POS from './pages/POS';
import PublicCalendarBooking from './pages/PublicCalendarBooking';
import RecurringInvoices from './pages/RecurringInvoices';
import RecurringInvoiceForm from './pages/RecurringInvoiceForm';
import Proformas from './pages/Proformas';
import ProformaForm from './pages/ProformaForm';
import OperationsDashboard from './pages/OperationsDashboard';
import AccountsDashboard from './pages/AccountsDashboard';
import SettingsDashboard from './pages/SettingsDashboard';
import Attendance from './pages/Attendance';
import KycDemo from './pages/KycDemo';
import PricingNew from './pages/PricingNew';
import Expenses from './pages/Expenses';
import ExpenseForm from './pages/ExpenseForm';
import WorklogPage from './pages/WorklogPage';
import Signup from './pages/Signup';
import Onboarding from './pages/Onboarding';
import ILPAdmin from './pages/ILPAdmin/ILPAdmin';
// Replace YOUR_GOOGLE_CLIENT_ID with your actual Google Client ID
// Load Google OAuth Client ID from environment variables (set in .env)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <AuthProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <Router>
          <Api401Redirector />
          <ScrollToTop />
          <div className="App">
            <AppContent menuOpen={menuOpen} toggleMenu={toggleMenu} setMenuOpen={setMenuOpen} />

          </div>
        </Router>
      </GoogleOAuthProvider>
    </AuthProvider>
  );
}

// Redirect to login on auth failures (e.g. missing Authorization header)
function Api401Redirector() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  React.useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const msg = error?.response?.data?.message;
        const shouldRedirect =
          status === 401 ||
          String(msg || '').toLowerCase().includes('authorization header required');

        if (shouldRedirect && location.pathname !== '/login') {
          try { logout?.(); } catch (_) { /* ignore */ }
          try {
            localStorage.removeItem('authToken');
            localStorage.removeItem('companyId');
          } catch (_) { /* ignore */ }
          navigate('/login', { replace: true });
        }

        return Promise.reject(error);
      }
    );

    return () => {
      try { api.interceptors.response.eject(interceptorId); } catch (_) { /* ignore */ }
    };
  }, [navigate, location.pathname, logout]);

  return null;
}

// Component to handle conditional layout based on route
function AppContent({ menuOpen, toggleMenu, setMenuOpen }) {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isHomePage = location.pathname === '/';
  const isKycDemoPage = location.pathname === '/kyc-demo';
  const isILPAdminPage = location.pathname === '/ilp-admin';
  const isOnboardingPage = location.pathname === '/onboarding';
  const isPlaceOrderPage = (() => {
    // Only treat unknown two-segment public URLs as order pages
    if (!/^\/[^/]+\/[^/]+$/.test(location.pathname)) return false;
    const first = location.pathname.split('/')[1]?.toLowerCase();
    const reserved = new Set([
      'login', 'signup', 'onboarding', 'order', 'orders', 'dashboard', 'account',
      'payment', 'privacy', 'refund', 'terms', 'contact', 'account-deletion',
      'pricing', 'products', 'company', 'customer', 'tasks', 'location', 'admin', 'pilot', 'custom-solutions',
      'invoices', 'invoice', 'quotes', 'quote', 'proformas', 'proforma', 'customers', 'inventory',
      'configurations', 'pos', 'operations', 'accounts', 'settings', 'kyc-demo', 'ilp-admin',
      'expenses', 'expense', 'billing', 'recurring-invoices', 'recurring-invoice'
    ]);
    return !reserved.has(first);
  })();
  // Hide public header/footer on complaint form routes
  const isComplaintFormPage = location.pathname === '/customer' || /^\/company\/[^/]+\/customer$/.test(location.pathname);


  // If user is authenticated and not on special pages, use sidebar layout
  if (isAuthenticated && !isKycDemoPage && !isILPAdminPage && !isPlaceOrderPage && !isOnboardingPage) {
    return <AuthenticatedLayout menuOpen={menuOpen} toggleMenu={toggleMenu} setMenuOpen={setMenuOpen} />;
  }

  const hidePublicChrome =
    isPlaceOrderPage || isComplaintFormPage || isKycDemoPage || isILPAdminPage || isOnboardingPage;

  // Otherwise use the public layout
  return (
    <ContactModalProvider>
      {!hidePublicChrome && <SiteNav />}

      <main
        className={`App-content${isHomePage ? ' home-page' : ''}${location.pathname.startsWith('/pricing') ? ' pricing-page' : ''}${hidePublicChrome ? ' chrome-hidden' : ''}`}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/calendar/:slug" element={<PublicCalendarBooking />} />
          <Route path=":companySlug/:templateSlug" element={<PlaceOrderGuard />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/account" element={<Account />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/account-deletion" element={<AccountDeletion />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/products" element={<OtherProducts />} />
          <Route path="/pricing-new" element={<PricingNew />} />
          <Route path="/company/:companyId/customer" element={<ComplaintForm />} />
          <Route path="/customer" element={<ComplaintForm />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/location" element={<LocationPage />} />
          <Route path="/configurations" element={<Configurations />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/custom-solutions" element={<Navigate to="/" replace />} />
          <Route path="/pilot" element={<Navigate to="/" replace />} />
          <Route path="/recurring-invoices" element={<RecurringInvoices />} />
          <Route path="/kyc-demo" element={<KycDemo />} />
          <Route path="/ilp-admin" element={<ILPAdmin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {!hidePublicChrome && <SiteFooter />}
    </ContactModalProvider>
  );
}

// Authenticated Layout with Sidebar
function AuthenticatedLayout({ menuOpen, toggleMenu, setMenuOpen }) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { company } = useAuth();
  const integrationConsents = usePendingIntegrationConsents();
  const [companyDetails, setCompanyDetails] = useState(null);

  // Ensure sidebar is closed on route changes to avoid unintended opens
  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, setMenuOpen]);

  // Fetch full company details to show logo/name in sidebar/mobile header
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!company?.id) return;
      try {
        const { data } = await api.get(`/api/companies/${company.id}`);
        if (!cancelled && data?.is_success) {
          setCompanyDetails(data.company);
        }
      } catch (e) {
        console.error('Failed to fetch company details (auth layout):', e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [company?.id]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className="authenticated-layout">
      {/* Sidebar Navigation */}
      <aside className={`sidebar ${menuOpen ? 'sidebar-open' : ''} ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-logo" onClick={() => setMenuOpen(false)}>
            <img
              src={companyDetails?.logo || company?.logo || logo}
              alt="BloomNav Logo"
              className="sidebar-logo-img"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = logo; }}
            />
            {!isCollapsed && <div className="sidebar-logo-text" style={{ fontSize: ((companyDetails?.name?.length > 10) ? 12 : 24), fontWeight: 500 }}>{companyDetails?.name || 'BloomNav'}</div>}
          </Link>
        </div>
        
        <nav className="sidebar-nav">
          <SidebarNavLinks setMenuOpen={setMenuOpen} isCollapsed={isCollapsed} toggleCollapse={toggleCollapse} />
        </nav>
      </aside>

      {/* Mobile Overlay */}
      {menuOpen && <div className="sidebar-overlay" onClick={() => setMenuOpen(false)}></div>}

      {/* Main Content Area */}
      <div className="main-layout">
        {/* Mobile Header with Menu Toggle */}
        <header className="mobile-header">
          <button className={`mobile-menu-toggle ${menuOpen ? 'active' : ''}`} onClick={toggleMenu}>
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className="mobile-logo">
            <img src={logo} alt="BloomNav Logo" />
            <span>BloomNav</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="authenticated-content">
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/account" element={<Account />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/inventory" element={<RequireFeature featureKey="INVENTORY"><Inventory /></RequireFeature>} />
            <Route path="/pos" element={<RequireFeature featureKey="POS"><POS /></RequireFeature>} />
            <Route path="/orders" element={<RequireFeature featureKey="ORDERS"><Orders /></RequireFeature>} />
            <Route path="/orders/:id" element={<RequireFeature featureKey="ORDERS"><OrderDetail /></RequireFeature>} />
            <Route path="/order/new" element={<RequireFeature featureKey="ORDERS"><OrderTemplateForm /></RequireFeature>} />
            <Route path="/order/:id/edit" element={<RequireFeature featureKey="ORDERS"><OrderTemplateForm /></RequireFeature>} />
            <Route path="/operations" element={<OperationsDashboard />} />
            <Route path="/attendance" element={<RequireFeature featureKey="ATTENDANCE"><Attendance /></RequireFeature>} />
            <Route path="/accounts" element={<RequireFeature featureKey="ACCOUNTS"><AccountsDashboard /></RequireFeature>} />
            <Route path="/settings" element={<SettingsDashboard />} />
            <Route path="/invoices" element={<RequireFeature featureKey="ACCOUNTS"><Invoices /></RequireFeature>} />
            <Route path="/quotes" element={<RequireFeature featureKey="ACCOUNTS"><Quotes /></RequireFeature>} />
            <Route path="/proformas" element={<RequireFeature featureKey="ACCOUNTS"><Proformas /></RequireFeature>} />
            <Route path="/proforma/new" element={<RequireFeature featureKey="ACCOUNTS"><ProformaForm /></RequireFeature>} />
            <Route path="/proforma/:id/edit" element={<RequireFeature featureKey="ACCOUNTS"><ProformaForm /></RequireFeature>} />
            <Route path="/recurring-invoices" element={<RequireFeature featureKey="ACCOUNTS"><RecurringInvoices /></RequireFeature>} />
            <Route path="/recurring-invoice/:id/edit" element={<RequireFeature featureKey="ACCOUNTS"><RecurringInvoiceForm /></RequireFeature>} />
            <Route path="/expenses" element={<RequireFeature featureKey="EXPENSES"><Expenses /></RequireFeature>} />
            <Route path="/expense/new" element={<RequireFeature featureKey="EXPENSES"><ExpenseForm /></RequireFeature>} />
            <Route path="/expense/:id/edit" element={<RequireFeature featureKey="EXPENSES"><ExpenseForm /></RequireFeature>} />
            <Route path="/invoice/new" element={<RequireFeature featureKey="ACCOUNTS"><InvoiceForm /></RequireFeature>} />
            <Route path="/quote/new" element={<RequireFeature featureKey="ACCOUNTS"><QuoteForm /></RequireFeature>} />
            <Route path="/invoice/:id/edit" element={<RequireFeature featureKey="ACCOUNTS"><InvoiceForm /></RequireFeature>} />
            <Route path="/quote/:id/edit" element={<RequireFeature featureKey="ACCOUNTS"><QuoteForm /></RequireFeature>} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/location" element={<RequireFeature featureKey="LOCATION"><LocationPage /></RequireFeature>} />
            <Route path="/worklog" element={<WorklogPage />} />
            <Route path="/configurations" element={<Configurations />} />
           {/*  <Route path="/admin" element={<Admin />} /> */}
            {/* Redirect any other routes to dashboard when authenticated */}
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </main>
        <IntegrationConsentModal
          consents={integrationConsents.consents}
          error={integrationConsents.error}
          onApprove={integrationConsents.approve}
          onDeny={integrationConsents.deny}
        />
      </div>
    </div>
  );
}

// Sidebar Navigation Links for Authenticated Users
function SidebarNavLinks({ setMenuOpen, isCollapsed, toggleCollapse }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [openGroups, setOpenGroups] = useState({});
  const { hasFeature } = useFeatureAccess();
  
  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const commerceChildren = [
    ...(hasFeature('ORDERS') ? [{ path: '/orders', label: 'Orders', icon: 'orders' }] : []),
    ...(hasFeature('POS') ? [{ path: '/pos', label: 'POS', icon: 'pos' }] : []),
    ...(hasFeature('INVENTORY') ? [{ path: '/inventory', label: 'Inventory', icon: 'inventory' }] : []),
  ];

  const revenueChildren = [
   
    ...(hasFeature('ACCOUNTS') ? [
      { path: '/accounts', label: 'Overview', icon: 'accounts' },
      { path: '/quotes', label: 'Quotes', icon: 'quote' },
      { path: '/proformas', label: 'Proformas', icon: 'proforma' },
      { path: '/invoices', label: 'Invoices', icon: 'invoice' },
      { path: '/recurring-invoices', label: 'Recurring Invoices', icon: 'recurring' },
    ] : []), { path: '/customers', label: 'Customers', icon: 'users' },
  ];

  const spendChildren = hasFeature('EXPENSES')
    ? [{ path: '/expenses', label: 'Expenses', icon: 'expenses' }]
    : [];

  const navItems = [
    { action: 'collapse', label: 'Close', icon: 'collapse' },

    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
    {
      type: 'group',
      label: 'Operations',
      icon: 'operations',
      children: [
        { path: '/operations', label: 'Overview', icon: 'operations' },
        { path: '/tasks', label: 'Tasks', icon: 'tasks' },
        { path: '/worklog', label: 'Worklog', icon: 'worklog' },
        ...(hasFeature('ATTENDANCE') ? [{ path: '/attendance', label: 'Attendance', icon: 'attendance' }] : []),
        ...(hasFeature('LOCATION') ? [{ path: '/location', label: 'Location', icon: 'location' }] : [])
      ]
    },
    ...(commerceChildren.length
      ? [{
        type: 'group',
        label: 'Sales',
        icon: 'commerce',
        children: commerceChildren
      }]
      : []),
    {
      type: 'group',
      label: 'Revenue',
      icon: 'accounts',
      children: revenueChildren
    },
    ...(spendChildren.length
      ? [{
        type: 'group',
        label: 'Spend',
        icon: 'spend',
        children: spendChildren
      }]
      : []),
    {
      type: 'group',
      label: 'Settings',
      icon: 'settings',
      children: [
        { path: '/account', label: 'Account', icon: 'account' },
        { path: '/configurations', label: 'Configurations', icon: 'configurations' }
      ]
    },
    { action: 'logout', label: 'Logout', icon: 'logout' },
  ];

  /* if (user?.role === 'admin') {
    navItems.push({ path: '/admin', label: 'Admin', icon: 'admin' });
  } */

  const renderIcon = (iconType) => {
    const iconProps = {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    };

    switch (iconType) {
      case 'dashboard':
        return <FiHome />;
      case 'location':
        return <FiMapPin />;
      case 'tasks':
        return <FiCheckSquare />;
      case 'account':
        return <FiUserOutline />;
      case 'admin':
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        );
      case 'logout':
        return <FiLogOut />;
      case 'collapse':
        return (
          <svg {...iconProps}>
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        );
        case 'collapse-open':
        return (
          <svg {...iconProps}>
            <polyline points="9,18 15,12 9,6"></polyline>
          </svg>
        );
      case 'users':
        return <FiUsersOutline />;
      case 'accounts':
        return <FiBookOpen />;
      case 'operations':
        return <FiActivity />;
      case 'quote':
        return <FiFileText />;
      case 'invoice':
        return <FiFile />;
      case 'proforma':
        return <FiClipboard />;
      case 'recurring':
        return <FiRefreshCw />;
      case 'inventory':
        return <FiBox />;
      case 'orders':
        return <FiShoppingCartOutline />;
      case 'pos':
        return <FiCreditCard />;
      case 'attendance':
        return <FiUserCheck />;
      case 'worklog':
        return <FiClipboard />;
      case 'settings':
        return <FiSettings />;
      case 'configurations':
        return <FiSliders />;
      case 'expenses':
        return <FiTrendingDown />;
      case 'commerce':
        return <FiShoppingCartOutline />;
      case 'spend':
        return <FiShoppingBag />;
      default:
        return null;
    }
  };
  
  return (
    <>
      {navItems.map((item) => {
        if (item.action === 'logout') {
          return (
            <button 
              key="logout"
              className="sidebar-link " 
              onClick={handleLogout}
              title={isCollapsed ? item.label : ''}
            >
              <span className="sidebar-icon">
                {renderIcon(item.icon)}
              </span>
              <span className="sidebar-text">{item.label}</span>
            </button>
          );
        } else if (item.action === 'collapse') {
          const collapseLabel = isCollapsed ? 'Open' : item.label;
          return (
            <button 
              key="collapse"
              className="sidebar-link collapse-link" 
              onClick={toggleCollapse}
              title={isCollapsed ? collapseLabel : ''}
            >
              <span className="sidebar-icon">
              {renderIcon(isCollapsed ? 'collapse-open' : item.icon)}
              </span>
              <span className="sidebar-text">{collapseLabel}</span>
            </button>
          );
        } else if (item.type === 'group') {
          return (
            <div key={item.label} className={`sidebar-group ${openGroups[item.label] ? 'open' : ''}`}>
              <button 
                className="sidebar-link" 
                onClick={() => { 
                  toggleGroup(item.label); 
                }}
                title={isCollapsed ? item.label : ''}
              >
                <span className="sidebar-icon">
                  {renderIcon(item.icon)}
                </span>
                <span className="sidebar-text">{item.label}</span>
              </button>
              {openGroups[item.label] && (
                <div className="sidebar-submenu">
                  {item.children.map((child) => (
                    <Link 
                      key={child.path}
                      to={child.path}
                      className={`sidebar-link sidebar-sublink ${isActive(child.path) ? 'active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                      title={isCollapsed ? child.label : ''}
                    >
                      <span className="sidebar-icon">
                        {renderIcon(child.icon)}
                      </span>
                      <span className="sidebar-text">{child.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        } else {
          return (
            <Link 
              key={item.path}
              to={item.path} 
              className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`} 
              onClick={() => setMenuOpen(false)}
              title={isCollapsed ? item.label : ''}
            >
              <span className="sidebar-icon">
                {renderIcon(item.icon)}
              </span>
              <span className="sidebar-text">{item.label}</span>
            </Link>
          );
        }
      })}
    </>
  );
}

export default App;

// Guard to prevent reserved paths (e.g., /customers/:id) from matching the public PlaceOrder route
function PlaceOrderGuard() {
  const { companySlug, templateSlug } = useParams();
  const [routeType, setRouteType] = useState('loading');
  const [formLinkData, setFormLinkData] = useState(null);

  React.useEffect(() => {
    let cancelled = false;

    // If it's a reserved path, don't bother checking form links
    const reserved = new Set([
      'login', 'signup', 'onboarding', 'order', 'orders', 'dashboard', 'account',
      'payment', 'privacy', 'refund', 'terms', 'contact', 'account-deletion',
      'pricing', 'company', 'customer', 'customers', 'tasks', 'location', 'admin',
      'pilot', 'custom-solutions', 'invoices', 'invoice', 'quotes', 'quote', 'proformas', 'proforma', 'inventory',
      'configurations', 'pos', 'kyc-demo', 'ilp-admin', 'expenses', 'expense', 'billing',
      'recurring-invoices', 'recurring-invoice'
    ]);
    if (reserved.has(String(companySlug || '').toLowerCase())) {
      setRouteType('reserved');
      return undefined;
    }

    if (!companySlug || !templateSlug) {
      setRouteType('notfound');
      return undefined;
    }

    (async () => {
      try {
        const res = await api.get(`/api/form-links/resolve/${companySlug}/${templateSlug}`);
        if (cancelled) return;
        if (res.data && res.data.is_success) {
          setFormLinkData(res.data);
          setRouteType('complaint');
          return;
        }
      } catch (_) {
        // Not a complaint form link - try order template next
      }

      try {
        const orderRes = await PublicOrderTemplatesAPI.getBySlug(companySlug, templateSlug);
        if (cancelled) return;
        if (orderRes?.template) {
          setRouteType('order');
          return;
        }
      } catch (_) {
        // Neither form link nor order template
      }

      if (!cancelled) setRouteType('notfound');
    })();

    return () => { cancelled = true; };
  }, [companySlug, templateSlug]);

  const hasToken = !!localStorage.getItem('authToken');

  if (routeType === 'loading') return null;

  if (routeType === 'reserved') {
    return hasToken ? null : <Navigate to="/login" replace />;
  }

  if (routeType === 'complaint') {
    return <ComplaintForm resolvedFormLink={formLinkData} />;
  }

  if (routeType === 'order') {
    return <PlaceOrder />;
  }

  return <NotFound />;
}
